import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AreaDisplayInfo } from "../../../Types/AreaDisplayInfo/AreaDisplayInfo";
import { downloadBlob } from "../../../Utils/Export/downloadBlob";
import { fetchAreaExportData } from "../../../Utils/Export/fetchAreaExportData";
import { openBlobInNewTab } from "../../../Utils/Export/openInNewTab";
import { getExporter } from "../../../Utils/Export/exporters";
import {
  DEFAULT_EXPORT_FIELD_VISIBILITY,
  EXPORT_TOGGLES,
  ExportColumnKey,
  ExportFieldVisibility,
  ExportFormat,
  ExportOptions,
  fullExportFieldVisibility,
} from "../../../Utils/Export/types";
import Button from "../Button/Button";
import ExportFormatChoiceGrid from "../ExportFormatChoice/ExportFormatChoiceGrid";
import {
  EXPORT_FORMAT_ORDER,
  getExportFormatUnavailableReason,
} from "../ExportFormatChoice/exportFormatDisplay";
import Popup from "../Popup/Popup";
import ToggleSlider from "../ToggleSlider/ToggleSlider";
import { EXPORT_POPUP_SHELL_STYLE } from "../../../Utils/Export/exportPopupShellStyle";
import styles from "./ExportButton.module.css";
import wizardStyles from "../LandingPages/BulkExportWizard/BulkExportWizard.module.css";

export type ExportButtonHandle = {
  trigger: () => void;
};

export type ExportButtonProps = {
  /** One or more evaluate area ids to export. */
  areaIds: string | string[];
  /** Optional row metadata (owner, due date, etc.) keyed by id for export columns. */
  areaDisplayInfos?: AreaDisplayInfo[];
  label?: string;
  /** Fixed format; if omitted, format is chosen in the export wizard. */
  format?: ExportFormat;
  disabled?: boolean;
  size?: "Small" | "Medium" | null;
  /** File name without extension; default includes current date. */
  filename?: string;
  /** Product segment in default filename: Mesma-{productName}-Export-date */
  productName?: string;
  onDone?: () => void;
  className?: string;
  /** Optional extra export options merged into the wizard output. */
  exportOptions?: ExportOptions;
  /** When true, only the wizard popup is rendered (no visible button). */
  hideTrigger?: boolean;
  /** Flat styling for use inside a grouped toolbar (no individual button chrome). */
  embedded?: boolean;
};

const WIZARD_FORMATS: ExportFormat[] = EXPORT_FORMAT_ORDER;

function normalizeIds(areaIds: string | string[]): string[] {
  const arr = Array.isArray(areaIds) ? areaIds : [areaIds];
  return arr.filter(Boolean);
}

function defaultBaseFilename(productName: string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `Mesma-${productName}-Export-${d.getFullYear()}-${pad(
    d.getMonth() + 1
  )}-${pad(d.getDate())}`;
}

const ExportButton = forwardRef<ExportButtonHandle, ExportButtonProps>(function ExportButton(
  {
    areaIds,
    areaDisplayInfos,
    label = "Export",
    format: formatProp,
    disabled = false,
    size = "Small",
    filename,
    productName = "Mesma",
    onDone,
    className,
    exportOptions,
    hideTrigger = false,
    embedded = false,
  },
  ref
) {
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState<2 | 3>(2);
  const [fieldVisibility, setFieldVisibility] = useState<ExportFieldVisibility>(() => ({
    ...DEFAULT_EXPORT_FIELD_VISIBILITY,
  }));
  const [error, setError] = useState<string | null>(null);

  const displayInfoById = useMemo(() => {
    if (!areaDisplayInfos?.length) return undefined;
    const m: Record<string, AreaDisplayInfo> = {};
    for (const a of areaDisplayInfos) {
      m[a.id] = a;
    }
    return m;
  }, [areaDisplayInfos]);

  const activeColumnCount = useMemo(
    () => EXPORT_TOGGLES.filter((k) => fieldVisibility[k]).length,
    [fieldVisibility]
  );

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setStep(2);
    setFieldVisibility({ ...DEFAULT_EXPORT_FIELD_VISIBILITY });
    setError(null);
    setLoading(false);
  }, []);

  const setColumnOn = useCallback((key: ExportColumnKey, on: boolean) => {
    setFieldVisibility((prev) => ({ ...prev, [key]: on }));
  }, []);

  const allColumnsSelected = useMemo(
    () => EXPORT_TOGGLES.every((k) => fieldVisibility[k]),
    [fieldVisibility]
  );

  const toggleSelectAllColumns = useCallback(() => {
    setFieldVisibility((prev) => {
      const allOn = EXPORT_TOGGLES.every((k) => prev[k]);
      const next = { ...prev };
      for (const k of EXPORT_TOGGLES) {
        next[k] = !allOn;
      }
      return next;
    });
  }, []);

  const runExport = useCallback(
    async (chosenFormat: ExportFormat) => {
      const unavailableReason = getExportFormatUnavailableReason(chosenFormat);
      if (unavailableReason) {
        return;
      }

      const ids = normalizeIds(areaIds);
      if (ids.length === 0) {
        toast.error("No areas selected to export.");
        return;
      }
      if (activeColumnCount === 0) {
        setError("Select at least one field to include in the export.");
        return;
      }

      const entry = getExporter(chosenFormat);
      if (!entry) {
        toast.error("This export format is not available.");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const vis = fullExportFieldVisibility(fieldVisibility);
        const data = await fetchAreaExportData(ids, { displayInfoById });
        const base = filename ?? defaultBaseFilename(productName);
        const blob = await entry.exporter(data, base, {
          ...exportOptions,
          fieldVisibility: vis,
        });
        const name = `${base}.${entry.meta.extension}`;
        if (chosenFormat === "html") {
          openBlobInNewTab(blob);
        } else {
          downloadBlob(blob, name);
        }
        toast.success(
          ids.length > 1
            ? `Exported ${ids.length} areas.`
            : "Export completed."
        );
        onDone?.();
        closeWizard();
      } catch (e) {
        console.error(e);
        toast.error("Export failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [
      activeColumnCount,
      areaIds,
      closeWizard,
      displayInfoById,
      exportOptions,
      fieldVisibility,
      filename,
      onDone,
      productName,
    ]
  );

  const handleClick = useCallback(() => {
    if (loading || disabled) return;
    setWizardOpen(true);
  }, [disabled, loading]);

  useImperativeHandle(ref, () => ({ trigger: handleClick }), [handleClick]);

  const border = disabled ? "#e0e0e0" : embedded ? "transparent" : "rgba(0,0,0,0.2)";
  const canNextStep2 = activeColumnCount > 0;
  const wizardFormats = formatProp ? [formatProp] : WIZARD_FORMATS;

  return (
    <>
      {!hideTrigger && (
        <Button
          className={`${styles.wrap} ${embedded ? styles.embedded : ""} ${className ?? ""}`}
          size={size === "Medium" ? null : size}
          colour={embedded ? "transparent" : "#FFF"}
          textColour="#000"
          border={border}
          disabled={disabled || loading}
          suppressHover={embedded}
          clickHandler={handleClick}
        >
          <span className={styles.inner}>
            {loading ? <span className={styles.spinner} aria-hidden /> : null}
            <span>{loading ? "Exporting…" : label}</span>
            {embedded && !loading ? (
              <img
                src="/img/document-upload.svg"
                alt=""
                className={styles.exportIcon}
                aria-hidden
              />
            ) : null}
          </span>
        </Button>
      )}

      <Popup
        isVisible={wizardOpen}
        onClose={closeWizard}
        style={EXPORT_POPUP_SHELL_STYLE}
      >
        <div className={wizardStyles.content}>
          <h3 className={wizardStyles.contentTitle}>Export data</h3>

          <div className={wizardStyles.stepMain}>
            {step === 2 ? (
              <>
                <p className={wizardStyles.stepHint}>
                  Choose which columns and blocks to include. Disabled items are
                  omitted from the export.
                </p>
                <div className={wizardStyles.listToolbar}>
                  <Button
                    size="Small"
                    colour="#FFF"
                    textColour="#000"
                    border="rgba(0,0,0,0.2)"
                    disabled={loading}
                    clickHandler={toggleSelectAllColumns}
                  >
                    {allColumnsSelected ? "Deselect all" : "Select all"}
                  </Button>
                </div>
                <div className={wizardStyles.toggleList}>
                  {EXPORT_TOGGLES.map((key) => (
                    <div key={key} className={wizardStyles.toggleRow}>
                      <span className={wizardStyles.toggleLabel}>{key}</span>
                      <ToggleSlider
                        isOn={fieldVisibility[key]}
                        onToggle={(next) => setColumnOn(key, next)}
                        disableConfirm
                        size="small"
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <p className={wizardStyles.stepHint}>
                  Choose a file format. Your column choices from the previous step
                  apply to Excel, Word, and the HTML report.
                </p>
                <div className={wizardStyles.formatRow}>
                  <ExportFormatChoiceGrid
                    variant="tiles"
                    formats={wizardFormats.filter((f) => !!getExporter(f))}
                    disabled={loading}
                    onChoose={(fmt) => void runExport(fmt)}
                  />
                  {loading ? (
                    <p className={wizardStyles.formatBusy} aria-live="polite">
                      Working on your export…
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>

          {error ? <p className={wizardStyles.error}>{error}</p> : null}

          <div className={wizardStyles.actions}>
            {step === 3 ? (
              <Button
                size="Small"
                colour="#FFF"
                textColour="#000"
                border="rgba(0,0,0,0.2)"
                disabled={loading}
                clickHandler={() => setStep(2)}
              >
                Back
              </Button>
            ) : null}
            {step === 2 ? (
              <Button
                size="Small"
                colour="rgb(36, 57, 79)"
                textColour="#FFF"
                border="transparent"
                disabled={loading || !canNextStep2}
                clickHandler={() => {
                  setError(null);
                  if (formatProp) {
                    void runExport(formatProp);
                    return;
                  }
                  setStep(3);
                }}
              >
                {formatProp ? "Export" : "Next"}
              </Button>
            ) : null}
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              disabled={loading}
              clickHandler={closeWizard}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Popup>
    </>
  );
});

export default ExportButton;
